import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useRouteMatch } from "react-router-dom";

import { Routes } from "constants/routes";
import { useActionHook, useDealParamsHooks, useNavigationHooks, useUrlHooks } from "hooks";
import { useNavigate } from "hooks";
import {
  useDealActions,
  useFolderMetaSelector,
  useFoldersSelect,
  useUserActions,
  useWorkspaceColumnsSelector
} from "store";

import {
  IFolder,
  IMetaDetails,
  IWorkspaceMetadata,
  WorkspaceFilters,
  WorkspaceFiltersFromDB
} from "../../../store/types";

export interface DealWorkspaceContextValue {
  // State
  folders: IFolder[];
  workspaceFolders: IFolder[];
  cloudMirrorFolders: IFolder[];
  emailFolder: IFolder | null;
  openedFolderIds: Set<number>;

  // Setters
  setOpenedFolderIds: React.Dispatch<React.SetStateAction<Set<number>>>;

  // Computed
  currentFolder: IFolder | null;
  folderTitle: string;
  isFolderOpen: (folderId: number) => boolean;

  // Actions
  onFolderArrowClick: (folderId: number) => void;
  openFolder: (folderId: number) => void;
  closeFolder: (folderId: number) => void;
  onFolderClick: ({ folderId, isCloudFolder }: { folderId: number; isCloudFolder: boolean }) => void;
  getChildFolders: (parentId: number, folders?: IFolder[]) => IFolder[];
  getRootFolders: (folders: IFolder[]) => IFolder[];
  onWorkspaceFolderClick: () => void;
  onWorkspaceArrowClick: () => void;
  isWorkspaceFolderOpen: boolean;

  folderMeta: Partial<IWorkspaceMetadata>;
  folderMetadataLoading: boolean;
  handleFetchNextPage: () => void;
  sortBy: { sort_by: string; sort: "asc" | "desc" } | null;
  setSortBy: (sortBy: { sort_by: string; sort: "asc" | "desc" } | null) => void;
  refetchFolderMetadata: () => Promise<void>;

  // Selection
  selectedItems: IMetaDetails[];
  setSelectedItems: React.Dispatch<React.SetStateAction<IMetaDetails[]>>;

  // Column visibility
  columnVisibility: Record<string, boolean>;
  toggleColumn: (columnId: string) => void;

  // Column order
  columnOrder: string[];
  reorderColumns: (order: string[]) => void;

  // Filters
  activeFilters: WorkspaceFilters;
  handleChangeFilter: (filters: WorkspaceFilters) => void;
  workspaceFiltersFromDB: WorkspaceFiltersFromDB;
  getWorkspaceFilters: (dealId: number) => Promise<WorkspaceFiltersFromDB>;

  /** True when viewing All Files or Recently Added (aggregate views). */
  isAggregateView: boolean;
}

export const DealWorkspaceContext = createContext<DealWorkspaceContextValue | null>(null);

const getAncestorIds = (folders: IFolder[], folderId: number): number[] => {
  const folder = folders.find((f) => f.id === folderId);
  if (!folder || folder.parent_id == null) return [];
  return [...getAncestorIds(folders, folder.parent_id), folder.parent_id];
};

interface DealWorkspaceProviderProps {
  children: React.ReactNode;
}

export const DealWorkspaceProvider: React.FC<DealWorkspaceProviderProps> = ({ children }) => {
  // --------------------Databox handlers--------------------
  const navigate = useNavigate();
  const { sectionId: folderIdFromUrl, dealId: dealIdFromUrl } = useDealParamsHooks();
  const workspaceColumns = useWorkspaceColumnsSelector();
  const { updateWorkspaceColumnsAction } = useUserActions();

  const { getFolderMetadataAction, getDealWorkspaceFiltersAction } = useDealActions();

  const folderMeta = useFolderMetaSelector();
  const allFolders = useFoldersSelect({});

  const workspaceFolders = useMemo(() => allFolders.filter((folder) => folder.kind === "folder"), [allFolders]);
  const cloudMirrorFolders = useMemo(() => allFolders.filter((folder) => folder.kind === "dataroom"), [allFolders]);
  const emailFolder = useMemo(() => allFolders.find((folder) => folder.kind === "email") ?? null, [allFolders]);

  const [openedFolderIds, setOpenedFolderIds] = useState<Set<number>>(() => new Set());
  const [isWorkspaceFolderOpen, setIsWorkspaceFolderOpen] = useState<boolean>(true);

  const onFolderClick = useCallback<DealWorkspaceContextValue["onFolderClick"]>(
    ({ folderId, isCloudFolder }) => {
      navigate({
        route: isCloudFolder ? "dealDataRoom" : "dealFolder",
        params: isCloudFolder
          ? { deal_id: dealIdFromUrl, folder_id: folderId }
          : { deal_id: dealIdFromUrl, folder_id: folderId },
        search: { section: folderId }
      });
    },
    [navigate, dealIdFromUrl]
  );

  const currentFolder = useMemo(
    () => allFolders.find((folder) => folder.id === folderIdFromUrl) ?? null,
    [allFolders, folderIdFromUrl]
  );

  const isFolderOpen = useCallback<DealWorkspaceContextValue["isFolderOpen"]>(
    (folderId) => openedFolderIds.has(folderId),
    [openedFolderIds]
  );

  const onFolderArrowClick = useCallback<DealWorkspaceContextValue["onFolderArrowClick"]>((folderId) => {
    setOpenedFolderIds((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  }, []);

  const openFolder = useCallback<DealWorkspaceContextValue["openFolder"]>((folderId) => {
    setOpenedFolderIds((prev) => (prev.has(folderId) ? prev : new Set(prev).add(folderId)));
  }, []);

  const closeFolder = useCallback<DealWorkspaceContextValue["closeFolder"]>((folderId) => {
    setOpenedFolderIds((prev) => {
      if (!prev.has(folderId)) return prev;
      const next = new Set(prev);
      next.delete(folderId);
      return next;
    });
  }, []);

  const getChildFolders = useCallback<DealWorkspaceContextValue["getChildFolders"]>(
    (parentId, folders = allFolders) => folders.filter((folder) => folder.parent_id === parentId),
    [allFolders]
  );

  const getRootFolders = useCallback<DealWorkspaceContextValue["getRootFolders"]>(
    (folders) => folders.filter((folder) => folder.parent_id === null),
    []
  );

  const onWorkspaceFolderClick = useCallback<DealWorkspaceContextValue["onWorkspaceFolderClick"]>(() => {
    navigate({ route: "dealWorkspace", params: { deal_id: dealIdFromUrl }, setQueriesMethod: "drop" });
  }, [navigate, dealIdFromUrl]);

  const onWorkspaceArrowClick = useCallback<DealWorkspaceContextValue["onWorkspaceArrowClick"]>(() => {
    setIsWorkspaceFolderOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    if (folderIdFromUrl == null || Number.isNaN(folderIdFromUrl)) return;
    const folderInWorkspace = workspaceFolders.some((folder) => folder.id === folderIdFromUrl);
    const folderInCloudMirror = cloudMirrorFolders.some((folder) => folder.id === folderIdFromUrl);
    const foldersToSearch = folderInWorkspace ? workspaceFolders : folderInCloudMirror ? cloudMirrorFolders : null;
    if (!foldersToSearch) return;
    const idsToOpen = getAncestorIds(foldersToSearch, folderIdFromUrl);
    if (idsToOpen.length > 0) {
      setOpenedFolderIds((prev) => new Set(Array.from(prev).concat(idsToOpen)));
    }
    if (folderInWorkspace) setIsWorkspaceFolderOpen(true);
  }, [folderIdFromUrl]);

  //-----------------selection handlers---------
  const [selectedItems, setSelectedItems] = useState<IMetaDetails[]>([]);

  useEffect(() => {
    setSelectedItems([]);
  }, [folderIdFromUrl]);

  //-----------------column visibility handlers---------
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(workspaceColumns);
  const toggleColumn = useCallback(
    (columnId: string) => {
      setColumnVisibility((prev) => {
        const next = { ...prev, [columnId]: !prev[columnId] };
        updateWorkspaceColumnsAction(next);
        return next;
      });
    },
    [updateWorkspaceColumnsAction]
  );

  //-----------------column order handlers---------
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const reorderColumns = useCallback((order: string[]) => {
    setColumnOrder(order);
  }, []);

  //-----------------filter handlers---------
  const [activeFilters, setActiveFilters] = useState<WorkspaceFilters>({});
  const { action: getWorkspaceFilters, data: workspaceFiltersFromDB = {} } =
    useActionHook(getDealWorkspaceFiltersAction);

  useEffect(() => {
    getWorkspaceFilters(dealIdFromUrl);
  }, []);

  const handleChangeFilter = useCallback((filters: WorkspaceFilters) => {
    setActiveFilters(filters);
  }, []);

  const filterPayloadBuilder = useCallback(
    (filters: WorkspaceFilters) => {
      const dateItem = filters?.last_updated?.[0];
      const dateRange = dateItem && typeof dateItem === "object" && "from" in dateItem ? dateItem : null;

      return {
        investor_ids: filters?.investor?.map((investor) => (investor as { id: number }).id) || undefined,
        file_type: filters?.file_type,
        tags: filters?.tag?.map((tag) => (tag as { id: number }).id) || undefined,
        classification: filters?.classification,
        file_status: filters?.file_status,
        document_type: filters?.document_type,
        currency: filters?.currency,
        source: filters?.source,
        shared_with: filters?.shared_with?.[0] ? (filters?.shared_with?.[0] === "Yes" ? true : false) : undefined,
        // Dataroom-only filters are sent in dataroom folders and aggregate views (no current folder), but
        // not in regular folders — where they stay in state (hidden) so they reapply on return.
        ...(!currentFolder || currentFolder.kind === "dataroom"
          ? { account_name: filters?.account_name, dataroom_document_type: filters?.dataroom_document_type }
          : {}),
        ...(filters?.last_updated ? { last_updated: [{ from: dateRange?.from ?? "", to: dateRange?.to ?? "" }] } : {})
      };
    },
    [currentFolder]
  );

  //-----------------workspace metadata handlers---------

  const isWorkspaceMatch = !!useRouteMatch({ path: Routes.dealWorkspace, exact: true });
  const isAllFilesMatch = !!useRouteMatch(Routes.dealWorkspaceAllFiles);
  const isFolderMatch = !!useRouteMatch(Routes.dealFolder);
  const isRecentlyAddedFilesMatch = !!useRouteMatch(Routes.dealWorkspaceRecentlyAdded);
  const isCloudMirrorMatch = !!useRouteMatch(Routes.dealDataRoom);

  const folderTitle = useMemo(() => {
    if (isFolderMatch) return currentFolder?.title || "";
    if (isCloudMirrorMatch) return currentFolder?.title || "";
    if (isWorkspaceMatch) return "Workspace";
    if (isAllFilesMatch) return "All Items";
    if (isRecentlyAddedFilesMatch) return "Recently Added";
    return "";
  }, [currentFolder, isFolderMatch, isCloudMirrorMatch, isWorkspaceMatch, isAllFilesMatch, isRecentlyAddedFilesMatch]);

  // Server-side sorting
  const [sortBy, setSortBy] = useState<{ sort_by: string; sort: "asc" | "desc" } | null>(null);
  const sortByRef = useRef(sortBy);
  sortByRef.current = sortBy;

  // Pagination state
  const hasMoreRef = useRef(false);
  const pageRef = useRef(1);
  const fetchingNextPageRef = useRef(false);
  const [recentlyAdded, setRecentlyAdded] = useState<boolean | undefined>(undefined);

  const getSectionId = useCallback(() => {
    const isAllFiles = isAllFilesMatch || isRecentlyAddedFilesMatch;
    return isAllFiles ? -1 : folderIdFromUrl || null;
  }, [isAllFilesMatch, isRecentlyAddedFilesMatch, folderIdFromUrl]);

  const { action: getFolderMetadata, loading: folderMetadataLoading } = useActionHook(
    async (params?: { recently_added?: boolean }) => {
      pageRef.current = 1;
      hasMoreRef.current = false;
      fetchingNextPageRef.current = false;
      setRecentlyAdded(params?.recently_added);
      const metadata = await getFolderMetadataAction({
        dealId: dealIdFromUrl,
        sectionId: getSectionId(),
        filters: filterPayloadBuilder(activeFilters) as unknown as WorkspaceFilters,
        recently_added: params?.recently_added,
        page: 1,
        ...(sortByRef.current ? sortByRef.current : {})
      });

      hasMoreRef.current = pageRef.current < (metadata.last_page ?? 1);
    }
  );

  const handleFetchNextPage = useCallback(async () => {
    if (fetchingNextPageRef.current || !hasMoreRef.current) return;
    fetchingNextPageRef.current = true;
    const nextPage = pageRef.current + 1;
    try {
      const metadata = await getFolderMetadataAction({
        dealId: dealIdFromUrl,
        sectionId: getSectionId(),
        filters: filterPayloadBuilder(activeFilters) as unknown as WorkspaceFilters,
        recently_added: recentlyAdded,
        page: nextPage,
        append: true,
        ...(sortByRef.current ? sortByRef.current : {})
      });
      pageRef.current = nextPage;
      if (!metadata.details?.length) {
        hasMoreRef.current = false;
        return;
      }
      hasMoreRef.current = pageRef.current < (metadata.last_page ?? 1);
    } catch {
      hasMoreRef.current = false;
    } finally {
      fetchingNextPageRef.current = false;
    }
  }, [recentlyAdded, dealIdFromUrl, getSectionId, filterPayloadBuilder, activeFilters, getFolderMetadataAction]);

  const refetchFolderMetadata = useCallback(async () => {
    pageRef.current = 1;
    hasMoreRef.current = false;
    fetchingNextPageRef.current = false;
    try {
      const metadata = await getFolderMetadataAction({
        dealId: dealIdFromUrl,
        sectionId: getSectionId(),
        filters: filterPayloadBuilder(activeFilters) as unknown as WorkspaceFilters,
        recently_added: recentlyAdded,
        page: 1,
        ...(sortByRef.current ? sortByRef.current : {})
      });
      hasMoreRef.current = pageRef.current < (metadata.last_page ?? 1);
    } catch {
      // ignore
    }
  }, [getFolderMetadataAction, dealIdFromUrl, getSectionId, activeFilters, filterPayloadBuilder, recentlyAdded]);

  useEffect(() => {
    if ((isFolderMatch || isCloudMirrorMatch) && folderIdFromUrl != null && !currentFolder) return;
    if (currentFolder?.kind === "email") return;
    const isAllFiles = isAllFilesMatch || isRecentlyAddedFilesMatch;
    setSelectedItems([]);
    getFolderMetadata({
      ...(isAllFiles ? { recently_added: isRecentlyAddedFilesMatch } : {})
    });
  }, [folderIdFromUrl, isAllFilesMatch, isRecentlyAddedFilesMatch, activeFilters, sortBy]);

  // --------------------Refetch from external trigger-------------------
  const { queryParams } = useUrlHooks();
  const { removeParams } = useNavigationHooks();

  useEffect(() => {
    if (queryParams.refetchWorkspace) {
      refetchFolderMetadata();
      removeParams(["refetchWorkspace"]);
    }
  }, [queryParams.refetchWorkspace]);

  // --------------------Context value-------------------

  const value = useMemo<DealWorkspaceContextValue>(
    () => ({
      folders: allFolders,
      workspaceFolders,
      cloudMirrorFolders,
      emailFolder,
      folderTitle,

      openedFolderIds,
      setOpenedFolderIds,
      currentFolder,
      isFolderOpen,
      onFolderArrowClick,
      openFolder,
      closeFolder,
      onFolderClick,
      getChildFolders,
      getRootFolders,
      onWorkspaceFolderClick,
      onWorkspaceArrowClick,
      isWorkspaceFolderOpen,

      folderMeta,
      folderMetadataLoading,
      handleFetchNextPage,
      sortBy,
      setSortBy,
      refetchFolderMetadata,
      selectedItems,
      setSelectedItems,

      columnVisibility,
      toggleColumn,

      columnOrder,
      reorderColumns,

      activeFilters,
      handleChangeFilter,
      workspaceFiltersFromDB,
      getWorkspaceFilters,

      isAggregateView: isAllFilesMatch || isRecentlyAddedFilesMatch
    }),
    [
      allFolders,
      workspaceFolders,
      cloudMirrorFolders,
      openedFolderIds,
      currentFolder,
      isFolderOpen,
      onFolderArrowClick,
      openFolder,
      closeFolder,
      onFolderClick,
      getChildFolders,
      getRootFolders,
      onWorkspaceFolderClick,
      onWorkspaceArrowClick,
      isWorkspaceFolderOpen,

      folderMeta,
      folderMetadataLoading,
      handleFetchNextPage,
      sortBy,
      setSortBy,
      refetchFolderMetadata,

      selectedItems,
      setSelectedItems,

      columnVisibility,
      toggleColumn,

      columnOrder,
      reorderColumns,

      activeFilters,
      handleChangeFilter,
      workspaceFiltersFromDB,
      getWorkspaceFilters,

      isAllFilesMatch,
      isRecentlyAddedFilesMatch
    ]
  );

  return <DealWorkspaceContext.Provider value={value}>{children}</DealWorkspaceContext.Provider>;
};

export const useDealWorkspace = (): DealWorkspaceContextValue => {
  const ctx = useContext(DealWorkspaceContext);
  if (ctx == null) {
    throw new Error("useDealWorkspace must be used within DealWorkspaceProvider");
  }
  return ctx;
};
